import React, { useMemo } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import { Link } from 'react-router-dom'

import { Campaign } from '../../../models/Outreach/types'
import { FunnelMetrics } from '../types/leasingSeason.types'
import {
    channelsFromCampaign,
    seasonCampaignsListPath,
} from '../Campaigns/utils/seasonCampaignUtils'
import { useSeasonCampaigns } from '../Campaigns/hooks/useSeasonCampaigns'
import { getPrimaryCampaignDisplayStatus } from '../utils/campaignDisplayStatus'
import { CampaignCard } from './components/CampaignCard'
import { CampaignPerformanceTable } from './components/CampaignPerformanceTable'
import { NewCampaignCard } from './components/NewCampaignCard'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    statGrid: {
        marginBottom: 0,
    },
    statTile: {
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        height: '100%',
        boxSizing: 'border-box',
    },
    statLabel: {
        fontSize: '0.625rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    statValue: {
        marginTop: theme.spacing(0.5),
        fontSize: '1.5rem',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        color: theme.palette.text.primary,
    },
    statValueHighlight: {
        color: theme.palette.primary.main,
    },
    statValueWarning: {
        color: '#b45309',
    },
    statHint: {
        marginTop: theme.spacing(0.5),
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    sectionHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.25),
    },
    sectionTitle: {
        fontSize: '1rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    sectionHint: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
    },
    manageLink: {
        marginTop: theme.spacing(0.5),
    },
}))

interface LeadGenerationHomeProps {
    seasonId?: number | null
    funnelMetrics?: FunnelMetrics
    primaryCampaigns?: Campaign[]
}

function customCampaignChannel(campaign: Campaign): 'email' | 'sms' | 'mixed' {
    const { email, sms } = channelsFromCampaign(campaign)
    if (email && sms) return 'mixed'
    if (sms) return 'sms'
    return 'email'
}

interface SummaryStat {
    label: string
    value: number
    hint: string
    highlight?: boolean
    warning?: boolean
}

export const LeadGenerationHome: React.FC<LeadGenerationHomeProps> = ({
    seasonId,
    funnelMetrics,
    primaryCampaigns = [],
}) => {
    const classes = useStyles()

    const {
        primaryEmail,
        primaryText,
        customCampaigns,
        allSeasonCampaigns,
        isLoading,
    } = useSeasonCampaigns(seasonId ?? undefined, primaryCampaigns)

    const metrics: FunnelMetrics = funnelMetrics ?? {
        prospects: 0,
        enriched: 0,
        inCampaign: 0,
        leads: 0,
        applications: 0,
    }

    const rollup = useMemo(() => {
        return allSeasonCampaigns.reduce(
            (acc, campaign) => {
                acc.enrolled += campaign.total_prospects ?? 0
                acc.pending += campaign.pending_prospects ?? 0
                acc.sent += campaign.total_sent ?? 0
                acc.opens += campaign.total_opens ?? 0
                acc.converted += campaign.total_converted ?? 0
                return acc
            },
            {
                enrolled: 0,
                pending: 0,
                sent: 0,
                opens: 0,
                converted: 0,
            },
        )
    }, [allSeasonCampaigns])

    const needsSetupCount = useMemo(() => {
        return allSeasonCampaigns.filter(
            (c) => getPrimaryCampaignDisplayStatus(c) === 'needs_setup',
        ).length
    }, [allSeasonCampaigns])

    const summaryStats: SummaryStat[] = [
        {
            label: 'Enrolled',
            value: rollup.enrolled,
            hint: 'Prospects currently in your season campaigns',
        },
        {
            label: 'Pending send',
            value: rollup.pending,
            hint: 'Enrolled prospects waiting for the next message',
        },
        {
            label: 'Messages sent',
            value: rollup.sent,
            hint: 'Emails and texts sent across season campaigns',
        },
        {
            label: 'Opens',
            value: rollup.opens,
            hint: 'Messages opened across season campaigns',
        },
        {
            label: 'Converted',
            value: rollup.converted,
            hint: 'Prospects converted from campaign outreach',
            highlight: true,
        },
        {
            label: 'Leads',
            value: metrics.leads,
            hint: 'People who engaged and entered your pipeline',
            highlight: true,
        },
        {
            label: 'Need setup',
            value: needsSetupCount,
            hint: 'Campaigns that still need configuration',
            warning: needsSetupCount > 0,
        },
    ]

    if (isLoading && seasonId != null) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Grid container spacing={2} className={classes.statGrid}>
                {summaryStats.map((stat) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={stat.label}>
                        <Box className={classes.statTile}>
                            <Typography className={classes.statLabel}>
                                {stat.label}
                            </Typography>
                            <Typography
                                className={`${classes.statValue}${
                                    stat.highlight
                                        ? ` ${classes.statValueHighlight}`
                                        : ''
                                }${
                                    stat.warning
                                        ? ` ${classes.statValueWarning}`
                                        : ''
                                }`}
                            >
                                {stat.value.toLocaleString()}
                            </Typography>
                            <Typography className={classes.statHint}>
                                {stat.hint}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            <CampaignPerformanceTable
                campaigns={allSeasonCampaigns}
                seasonId={seasonId}
            />

            <Box className={classes.section}>
                <Box className={classes.sectionHeader}>
                    <Typography className={classes.sectionTitle}>
                        Configure campaigns
                    </Typography>
                    <Typography className={classes.sectionHint}>
                        Set up primary and custom outreach sequences for this
                        season.
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <CampaignCard
                            title="Primary Email Outreach"
                            channel="email"
                            campaign={primaryEmail}
                            seasonId={seasonId}
                            alwaysShowConfigure
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <CampaignCard
                            title="Primary Text Outreach"
                            channel="sms"
                            campaign={primaryText}
                            seasonId={seasonId}
                            alwaysShowConfigure
                        />
                    </Grid>
                    {customCampaigns.map((campaign) => (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            key={campaign.id}
                        >
                            <CampaignCard
                                title={campaign.name}
                                channel={customCampaignChannel(campaign)}
                                campaign={campaign}
                                seasonId={seasonId}
                                description="Custom outreach sequence for this season."
                            />
                        </Grid>
                    ))}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <NewCampaignCard seasonId={seasonId} />
                    </Grid>
                </Grid>

                {allSeasonCampaigns.length > 0 && (
                    <Box className={classes.manageLink}>
                        <Button
                            component={Link}
                            to={seasonCampaignsListPath(seasonId ?? undefined)}
                            endIcon={<ArrowForwardIcon />}
                            color="primary"
                            size="small"
                        >
                            Manage all campaigns
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
